import { useQuery } from "@tanstack/react-query";
import { type ReactNode } from "react";
import { Link } from "react-router-dom";

import { Badge, Card, Skeleton } from "./ui";
import { CasesIcon, MessagesIcon, TasksIcon, TodayIcon } from "./icons";
import {
  type ApiCrossMessage,
  type ApiStaleCase,
  type ApiTask,
  fetchMe,
  getDashboardActiveCases,
  getDashboardTodayTasks,
  getTodayTasks,
  listAllMessages,
  listFollowUps,
  listUnlinkedMessages,
} from "../lib/api";

type Tone = "primary" | "danger" | "warning" | "success";

const TONE_BG: Record<Tone, string> = {
  primary: "bg-primary-bg text-primary",
  danger: "bg-danger-bg text-danger",
  warning: "bg-warning-bg text-warning",
  success: "bg-success-bg text-success",
};

export function DashboardHome(): JSX.Element {
  return (
    <div className="space-y-5">
      <KpiRow />
      <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-4">
        <TodaySchedulePanel />
        <UpcomingTasksPanel />
        <RecentMessagesPanel />
        <AiSummaryPanel />
      </div>
      <FollowUpPanel />
      <ConfirmBanner />
    </div>
  );
}

// ─── KPI ───────────────────────────────────────────────
function KpiRow(): JSX.Element {
  const { data: me } = useQuery({ queryKey: ["me"], queryFn: fetchMe, staleTime: 300_000 });
  const tasks = useQuery({ queryKey: ["dashboard", "today-tasks"], queryFn: getDashboardTodayTasks });
  const active = useQuery({ queryKey: ["dashboard", "active-cases"], queryFn: getDashboardActiveCases });
  const follow = useQuery({ queryKey: ["follow-ups"], queryFn: listFollowUps });
  const unlinked = useQuery({
    queryKey: ["admin", "unlinked", "count"],
    queryFn: () => listUnlinkedMessages({ limit: 100 }),
    enabled: me?.user?.role === "admin",
    retry: false,
  });
  return (
    <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
      <KpiCard
        label="今日のタスク"
        value={tasks.data?.incomplete}
        sub={tasks.data ? `完了 ${tasks.data.done} / 全 ${tasks.data.total}` : undefined}
        icon={<TasksIcon className="h-5 w-5" />}
        tone="primary"
        loading={tasks.isLoading}
      />
      <KpiCard
        label="進行中案件"
        value={active.data}
        icon={<CasesIcon className="h-5 w-5" />}
        tone="danger"
        loading={active.isLoading}
      />
      <KpiCard
        label="追客対象"
        value={follow.data?.length}
        sub="7日以上 未接触"
        icon={<TodayIcon className="h-5 w-5" />}
        tone="warning"
        loading={follow.isLoading}
      />
      <KpiCard
        label="未紐付けメッセージ"
        value={me?.user?.role === "admin" ? unlinked.data?.length : 0}
        icon={<MessagesIcon className="h-5 w-5" />}
        tone="primary"
        loading={me?.user?.role === "admin" && unlinked.isLoading}
      />
    </div>
  );
}

function KpiCard({
  label,
  value,
  sub,
  icon,
  tone,
  loading,
}: {
  label: string;
  value: number | undefined;
  sub?: string | undefined;
  icon: ReactNode;
  tone: Tone;
  loading?: boolean;
}): JSX.Element {
  return (
    <Card className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-text-secondary">{label}</span>
        <span className={`flex h-9 w-9 items-center justify-center rounded-lg ${TONE_BG[tone]}`}>
          {icon}
        </span>
      </div>
      {loading === true ? (
        <Skeleton className="h-9 w-16" />
      ) : (
        <p className="flex items-baseline gap-1">
          <span className="text-3xl font-bold tabular-nums text-text-primary">{value ?? "—"}</span>
          <span className="text-sm text-text-secondary">件</span>
        </p>
      )}
      <p className="min-h-4 text-xs text-text-tertiary">{sub ?? ""}</p>
    </Card>
  );
}

// ─── Panels ────────────────────────────────────────────
function Panel({
  title,
  to,
  linkLabel,
  children,
}: {
  title: string;
  to?: string | undefined;
  linkLabel?: string | undefined;
  children: ReactNode;
}): JSX.Element {
  return (
    <Card className="flex flex-col">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-text-primary">{title}</h3>
        {to !== undefined && (
          <Link to={to} className="shrink-0 text-xs font-medium text-primary hover:text-primary-hover">
            {linkLabel ?? "すべて見る"} →
          </Link>
        )}
      </div>
      <div className="mt-3 flex-1 space-y-2.5">{children}</div>
    </Card>
  );
}

function EmptyLine({ text }: { text: string }): JSX.Element {
  return <p className="py-4 text-center text-xs text-text-tertiary">{text}</p>;
}

function TodaySchedulePanel(): JSX.Element {
  // 予定の横断取得 API が未整備のためプレースホルダ表示。
  return (
    <Panel title="今日の予定" linkLabel="カレンダー">
      <div className="space-y-2.5 opacity-60">
        {["09:30 内見 同行", "13:00 物件調査", "16:00 契約書 確認"].map((s) => {
          const [time, ...rest] = s.split(" ");
          return (
            <div key={s} className="flex items-center gap-2 text-sm">
              <span className="w-12 shrink-0 font-medium tabular-nums text-text-secondary">{time}</span>
              <span className="truncate text-text-primary">{rest.join(" ")}</span>
            </div>
          );
        })}
      </div>
      <Badge variant="neutral">カレンダー連携は準備中</Badge>
    </Panel>
  );
}

function UpcomingTasksPanel(): JSX.Element {
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard", "upcoming-tasks"],
    queryFn: () => getTodayTasks({ include_overdue: true, statuses: ["open", "in_progress"] }),
  });
  const rows = (data ?? []).slice(0, 4);
  return (
    <Panel title="期限近接タスク" to="/tasks">
      {isLoading && <Skeleton className="h-20 w-full" />}
      {!isLoading && rows.length === 0 && <EmptyLine text="対象のタスクはありません" />}
      {rows.map((task: ApiTask) => (
        <div key={task.id} className="flex items-start gap-2 text-sm">
          <span
            className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${
              task.priority === "high" ? "bg-danger" : task.priority === "medium" ? "bg-warning" : "bg-text-tertiary"
            }`}
          />
          <span className="min-w-0 flex-1 truncate text-text-primary">{task.title}</span>
          {task.due_date !== null && (
            <span className="shrink-0 text-xs text-text-tertiary">{shortDate(task.due_date)}</span>
          )}
        </div>
      ))}
    </Panel>
  );
}

function RecentMessagesPanel(): JSX.Element {
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard", "recent-messages"],
    queryFn: () => listAllMessages({ limit: 4, direction: "inbound" }),
  });
  const rows = data ?? [];
  return (
    <Panel title="新着メッセージ" to="/messages">
      {isLoading && <Skeleton className="h-20 w-full" />}
      {!isLoading && rows.length === 0 && <EmptyLine text="新着メッセージはありません" />}
      {rows.map((m: ApiCrossMessage) => (
        <div key={m.id} className="space-y-0.5">
          <div className="flex items-center justify-between gap-2">
            <span className="min-w-0 flex-1 truncate text-sm font-medium text-text-primary">
              {m.sender || m.customer_name || m.case_title || "（送信者不明）"}
            </span>
            <span className="shrink-0 text-[11px] text-text-tertiary">{shortDate(m.created_at)}</span>
          </div>
          <p className="truncate text-xs text-text-secondary">{m.subject || m.content}</p>
        </div>
      ))}
    </Panel>
  );
}

function AiSummaryPanel(): JSX.Element {
  return (
    <Panel title="AIサマリー">
      <ul className="space-y-2 text-sm text-text-secondary opacity-70">
        <li>・本日の重点案件と次アクションを要約します</li>
        <li>・追客が滞っている案件を検知します</li>
        <li>・新着メッセージの要点を抽出します</li>
      </ul>
      <Badge variant="primary">AI機能は準備中</Badge>
    </Panel>
  );
}

function FollowUpPanel(): JSX.Element {
  const { data, isLoading } = useQuery({ queryKey: ["follow-ups"], queryFn: listFollowUps });
  const rows = (data ?? []).slice(0, 6);
  return (
    <Panel title="フォローアップ候補" to="/follow-ups" linkLabel="もっと見る">
      {isLoading && <Skeleton className="h-16 w-full" />}
      {!isLoading && rows.length === 0 && <EmptyLine text="追客が必要な案件はありません" />}
      <div className="grid gap-2.5 md:grid-cols-2 lg:grid-cols-3">
        {rows.map((c: ApiStaleCase) => (
          <Link
            key={c.case_id}
            to={`/cases/${c.case_id}`}
            className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2 transition hover:bg-bg"
          >
            <span className="min-w-0 flex-1 truncate text-sm text-text-primary">{c.title}</span>
            <Badge variant={c.days_since_contact >= 14 ? "danger" : "warning"}>
              {c.days_since_contact}日
            </Badge>
          </Link>
        ))}
      </div>
    </Panel>
  );
}

function ConfirmBanner(): JSX.Element {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-warning/30 bg-warning-bg px-4 py-3">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-warning/15 text-warning">
        <ShieldIcon />
      </span>
      <p className="text-sm text-text-secondary">
        送信・契約・金額などの重要操作は、<strong className="text-text-primary">必ず人間が確認してから</strong>
        実行されます。
      </p>
    </div>
  );
}

function ShieldIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5" aria-hidden>
      <path d="M12 3l7 3v6c0 4-3 7-7 8-4-1-7-4-7-8V6z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  );
}

function shortDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat("ja-JP", { month: "2-digit", day: "2-digit" }).format(new Date(iso));
  } catch {
    return "";
  }
}
